import { useState, useEffect, useCallback } from 'react'
import { apiFetch } from './client'

export interface MyTeam {
  id: string
  name: string
  slug: string
  logoUrl?: string
  primaryColor?: string
  accentColor?: string
  role: string
  gameId: string
  gameName: string
  orgId: string
  orgName: string
  orgSlug: string
  orgType: string
  /** The owning org's logo — what a team with no logo of its own wears. */
  orgLogoUrl?: string
  /** The owning org's brand colour — what a team with no colour of its own wears. */
  orgPrimaryColor?: string
  /** A walk-up team with no owner yet that you could ask the league for. */
  ownerRequestable?: boolean
  /** You've asked to own it; the league hasn't answered. */
  ownerRequestPending?: boolean
}

interface ApiTeamItem {
  id: string
  name: string
  slug: string
  logo_url: string
  organization_logo_url?: string
  organization_primary_color?: string
  primary_color?: string
  secondary_color?: string
  accent_color?: string
  role: string
  joined_at: string
  game_id: string
  game_name: string
  organization_id: string
  organization_name: string
  organization_slug: string
  organization_type: string
  owner_requestable?: boolean
  owner_request_pending?: boolean
}

interface ApiResponse {
  teams: ApiTeamItem[]
  count: number
}

// Simple event target so any code can trigger a refetch of the team list.
const myTeamsEvents = new EventTarget()

/** Call this after joining/leaving a team to update all useMyTeams consumers. */
export function refreshMyTeams() {
  myTeamsEvents.dispatchEvent(new Event('refresh'))
}

/**
 * Hook that fetches the current user's teams (roster memberships across all orgs).
 * Returns empty array if not authenticated or on error.
 * Call refreshMyTeams() anywhere to trigger a refetch.
 */
export function useMyTeams(isAuthenticated: boolean): MyTeam[] {
  const [teams, setTeams] = useState<MyTeam[]>([])
  const [version, setVersion] = useState(0)

  // Listen for refresh events
  useEffect(() => {
    const handler = () => setVersion((v) => v + 1)
    myTeamsEvents.addEventListener('refresh', handler)
    return () => myTeamsEvents.removeEventListener('refresh', handler)
  }, [])

  const fetchTeams = useCallback(() => {
    if (!isAuthenticated) {
      setTeams([])
      return
    }

    let cancelled = false
    apiFetch<ApiResponse>('/teams/mine')
      .then((data) => {
        if (cancelled) return
        setTeams(
          data.teams.map((t) => ({
            id: t.id,
            name: t.name,
            slug: t.slug,
            logoUrl: t.logo_url || undefined,
            primaryColor: t.primary_color || undefined,
            accentColor: t.accent_color || undefined,
            role: t.role,
            gameId: t.game_id,
            gameName: t.game_name,
            orgId: t.organization_id,
            orgName: t.organization_name,
            orgSlug: t.organization_slug,
            orgType: t.organization_type,
            orgLogoUrl: t.organization_logo_url || undefined,
            orgPrimaryColor: t.organization_primary_color || undefined,
            ownerRequestable: t.owner_requestable || undefined,
            ownerRequestPending: t.owner_request_pending || undefined,
          })),
        )
      })
      .catch(() => {
        if (!cancelled) setTeams([])
      })

    return () => { cancelled = true }
  }, [isAuthenticated])

  useEffect(() => fetchTeams(), [fetchTeams, version])

  return teams
}
